import { DraftsOutlined } from "@mui/icons-material";
import { Box, Card, CardContent, Divider, Grid, Typography } from "@mui/material";
import { formatDateLong } from "@/utils/date-format";
import GfgAvatar from "./GfgAvatar";
import WidgedHeader from "./WidgedHeader";
import WidgetEmptyState from "./WidgetEmptyState";

interface ThankYouNotesProps {
  notes: {
    id: number;
    sender: string;
    message: string;
    for: string;
    submitted: string;
  }[];
  /** The real total, independent of how many `notes` were handed in for preview — the desktop
   *  layout passes only a 2-item slice for the card, and the header badge must not shrink to
   *  match it. Falls back to notes.length so a caller with the full list still works unchanged. */
  totalCount?: number;
}

const ThankYouNotes = ({ notes, totalCount }: ThankYouNotesProps) => {
  return (
    <Grid size={{ xs: 12, md: 4 }} sx={{ display: "flex", flexDirection: "column" }}>
      <WidgedHeader
        title="Thank You Notes"
        count={totalCount ?? notes.length}
        viewAllHref="/thanks-report"
      />
      <Card
        sx={{
          flex: 1,
          minHeight: 300,
          border: "1px solid #e5e5e5",
          boxShadow: "none",
        }}
        className="rounded-2xl px-3 py-1"
      >
        <CardContent
          className="flex flex-col items-center justify-start gap-1"
          sx={{ height: "100%" }}
        >
          {notes.length === 0 && (
            <WidgetEmptyState
              icon={<DraftsOutlined />}
              title="No thank you notes yet"
              subtitle="Notes from recipients who redeem their gifts will appear here."
            />
          )}
          {notes.map((note, index) => (
            <Box key={note.id} sx={{ width: "100%" }}>
              <Grid size={{ xs: 12 }} className="flex flex-row items-center gap-2 h-12">
                <GfgAvatar seed={note.id} size={48} />
                <Grid>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "16px",
                      lineHeight: 1,
                      color: (theme) => theme.palette.text.primary,
                    }}
                  >
                    {note.sender}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "20px",
                      color: (theme) => theme.palette.text.secondary,
                    }}
                  >
                    {formatDateLong(note.submitted)}
                  </Typography>
                </Grid>
              </Grid>
              <Grid className="flex flex-col justify-start items-start w-full mb-4">
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: "16px",
                    lineHeight: "24px",
                    color: (theme) => theme.palette.text.primary,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    my: 1.5,
                  }}
                >
                  "{note.message}"
                </Typography>
              </Grid>
              {index < notes.length - 1 && <Divider sx={{ marginBottom: 3 }} />}
            </Box>
          ))}
        </CardContent>
      </Card>
    </Grid>
  );
};

export default ThankYouNotes;
